import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Space, Popconfirm, Card, Typography, Select, Input, Switch, Tag, InputNumber, Alert } from 'antd';
import { ApiOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import ProviderWizard from '../components/ProviderWizard';
import ModelWizard from '../components/ModelWizard';
import PricingRulesModal, { parsePricingRules } from '../components/PricingRulesModal';

const { Title, Text } = Typography;

// 固定类型映射（中英文）
const TYPE_MAPPING = {
  text: 'Text / 对话',
  image: 'Image / 图像',
  embeddings: 'Embeddings / 嵌入',
  audio: 'Audio / 语音',
  video: 'Video / 视频',
  rerank: 'Rerank / 重排序',
  speech: 'Speech / 语音合成',
  transcription: 'Transcription / 语音识别',
};
const FIXED_TYPES = Object.keys(TYPE_MAPPING);

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);

  // 向导模式状态
  const [providerWizardVisible, setProviderWizardVisible] = useState(false);
  const [modelWizardVisible, setModelWizardVisible] = useState(false);
  const [wizardEditingProvider, setWizardEditingProvider] = useState(null);
  const [wizardEditingModel, setWizardEditingModel] = useState(null);

  // 模型级备用模型配置状态
  const [fallbackModalVisible, setFallbackModalVisible] = useState(false);
  const [pricingRulesVisible, setPricingRulesVisible] = useState(false);
  const [pricingRulesModel, setPricingRulesModel] = useState(null);
  const [fallbackEditingModel, setFallbackEditingModel] = useState(null);
  const [fallbackModels, setFallbackModels] = useState({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
  const [selectedFallbackType, setSelectedFallbackType] = useState('text');
  const [fallbackLoading, setFallbackLoading] = useState(false);

  // 模型搜索状态
  const [modelSearchText, setModelSearchText] = useState('');
  const [modelTypeFilter, setModelTypeFilter] = useState(null);
  const [modelStatusFilter, setModelStatusFilter] = useState(null);
  const [modelProviderFilter, setModelProviderFilter] = useState(null);
  const [modelHotFilter, setModelHotFilter] = useState(null);
  const [modelBillingFilter, setModelBillingFilter] = useState(null);
  const [modelFallbackFilter, setModelFallbackFilter] = useState(null); // 备用模型筛选
  const [modelParamsFilter, setModelParamsFilter] = useState(null); // 特殊参数筛选
  const [modelPageSize, setModelPageSize] = useState(10);
  const [modelCurrentPage, setModelCurrentPage] = useState(1);

  useEffect(() => {
    fetchProviders();
    fetchModels();
  }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getProviders();
      if (response.code === 0) {
        const sortedList = (response.data.list || []).sort((a, b) => a.id - b.id);
        setProviders(sortedList);
      }
    } catch (error) {
      message.error('获取供应商列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await adminAPI.getModelConfigs();
      if (response.code === 0) {
        setModels(response.data.list || []);
      }
    } catch (error) {
      console.error('获取模型列表失败');
    }
  };

  const handleDeleteProvider = async (id) => {
    try {
      const response = await adminAPI.deleteProvider(id);
      if (response.code === 0) {
        message.success('删除成功');
        fetchProviders();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleDeleteModel = async (id) => {
    try {
      const response = await adminAPI.deleteModelConfig(id);
      if (response.code === 0) {
        message.success('删除成功');
        fetchModels();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 切换热门状态
  const handleToggleHot = async (id, isHot) => {
    try {
      const response = await adminAPI.setModelHot(id, isHot);
      if (response.code === 0) {
        message.success(isHot ? '已设为热门' : '已取消热门');
        fetchModels();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '操作失败');
        fetchModels();
      }
    } catch (error) {
      message.error('操作失败: ' + (error.message || error));
      fetchModels();
    }
  };

  // 切换启用/禁用状态
  const handleToggleStatus = async (id, checked) => {
    const status = checked ? 'active' : 'inactive';
    try {
      const response = await adminAPI.setModelStatus(id, status);
      if (response.code === 0) {
        message.success(checked ? '已启用' : '已禁用');
        fetchModels();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '操作失败');
        fetchModels();
      }
    } catch (error) {
      message.error('操作失败: ' + (error.message || error));
      fetchModels();
    }
  };

  // 打开模型级备用模型配置弹窗
  const handleOpenFallbackModal = (model) => {
    setFallbackEditingModel(model);
    // 解析已有的备用模型配置
    try {
      const existingFallback = model.fallback_models ? JSON.parse(model.fallback_models) : {};
      setFallbackModels({
        text: existingFallback.text || [],
        image: existingFallback.image || [],
        embeddings: existingFallback.embeddings || [],
        audio: existingFallback.audio || [],
        video: existingFallback.video || [],
        rerank: existingFallback.rerank || [],
        speech: existingFallback.speech || [],
        transcription: existingFallback.transcription || [],
      });
    } catch (e) {
      setFallbackModels({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
    }
    setFallbackModalVisible(true);
  };

  // 保存模型级备用模型配置
  const handleSaveFallback = async () => {
    if (!fallbackEditingModel) return;
    setFallbackLoading(true);
    try {
      const response = await adminAPI.updateModelFallback(fallbackEditingModel.id, fallbackModels);
      if (response.code === 0) {
        message.success('备用模型配置已保存');
        setFallbackModalVisible(false);
        fetchModels();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败: ' + (error.message || error));
    } finally {
      setFallbackLoading(false);
    }
  };

  // 清除模型级备用模型配置
  const handleClearFallback = async () => {
    if (!fallbackEditingModel) return;
    setFallbackLoading(true);
    try {
      const response = await adminAPI.updateModelFallback(fallbackEditingModel.id, {});
      if (response.code === 0) {
        message.success('备用模型配置已清除');
        setFallbackModels({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
        fetchModels();
        await adminAPI.refreshGatewayModels().catch(() => {});
      } else {
        message.error(response.message || '清除失败');
      }
    } catch (error) {
      message.error('清除失败: ' + (error.message || error));
    } finally {
      setFallbackLoading(false);
    }
  };

  // 获取类型显示名称（中英文）
  const getTypeName = (type) => {
    return TYPE_MAPPING[type] || type || '-';
  };

  // 获取类型显示名称（仅中文）
  const getTypeNameCn = (type) => {
    const names = {
      text: '对话', image: '图像', embeddings: '嵌入', audio: '语音', video: '视频',
      rerank: '重排序', speech: '语音合成', transcription: '语音识别', chat: '对话'
    };
    return names[type] || type || '-';
  };

  const providerColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '供应商名称', dataIndex: 'name', width: 120 },
    { title: '显示名称', dataIndex: 'display_name', width: 150 },
    { title: 'API地址', dataIndex: 'api_base', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (status) => (
        <span style={{ color: status === 'active' ? '#52c41a' : '#ff4d4f' }}>
          {status === 'active' ? '启用' : '禁用'}
        </span>
      ),
    },
    { title: '优先级', dataIndex: 'priority', width: 80 },
    {
      title: '操作', width: 150, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => {
            setWizardEditingProvider(record);
            setProviderWizardVisible(true);
          }}>编辑</Button>
          <Popconfirm title="确定要删除此供应商吗？" onConfirm={() => handleDeleteProvider(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const modelColumns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '模型标识', dataIndex: 'model_name', width: 120 },
    { title: '显示名称', dataIndex: 'display_name', width: 110 },
    { title: '供应商', dataIndex: ['provider', 'display_name'], width: 90 },
    {
      title: '类型', dataIndex: 'type', width: 60,
      render: (t) => {
        const colors = { text: 'blue', image: 'green', embeddings: 'purple', audio: 'orange', video: 'red', rerank: 'cyan', speech: 'geekblue', transcription: 'gold', chat: 'blue', embedding: 'purple' };
        return <Tag color={colors[t] || 'default'}>{getTypeNameCn(t)}</Tag>;
      }
    },
    {
      title: '计费方式', dataIndex: 'billing_type', width: 90,
      render: (t) => {
        const names = { per_token: 'Token', per_call: '按次', per_char: '字符', per_second: '按秒', free: '免费' };
        const colors = { per_token: 'blue', per_call: 'orange', per_char: 'purple', per_second: 'geekblue', free: 'green' };
        return <Tag color={colors[t] || 'default'}>{names[t] || t}</Tag>;
      }
    },
    { title: '计费价格', width: 200,
      render: (_, record) => {
        const { billing_type, input_price, output_price, billing_rate } = record;
        const ruleCount = parsePricingRules(record.metadata).length;
        switch (billing_type) {
          case 'per_token':
            return (
              <span>
                输入 ${parseFloat(input_price || 0).toFixed(6)} / 输出 ${parseFloat(output_price || 0).toFixed(6)} / 1K
                {ruleCount > 0 && <Tag color="orange" style={{ marginLeft: 4 }}>{ruleCount}条规则</Tag>}
              </span>
            );
          case 'per_call':
            return <span>${parseFloat(billing_rate || 0).toFixed(4)} / 次</span>;
          case 'per_char':
            return <span>${parseFloat(billing_rate || 0).toFixed(6)} / 字符</span>;
          case 'per_second':
            return <span>${parseFloat(billing_rate || 0).toFixed(6)} / 秒</span>;
          case 'free':
            return <Tag color="green">免费</Tag>;
          default:
            return `$${parseFloat(input_price || 0).toFixed(6)}`;
        }
      }
    },
    {
      title: '热门', dataIndex: 'is_hot', width: 70,
      render: (v, record) => (
        <Switch
          size="small"
          checked={v}
          onChange={(checked) => handleToggleHot(record.id, checked)}
        />
      )
    },
    { title: '输入价', dataIndex: 'input_price', width: 100, render: (v) => v ? `$${parseFloat(v).toFixed(6)}` : '-' },
    { title: '输出价', dataIndex: 'output_price', width: 100, render: (v) => v ? `$${parseFloat(v).toFixed(6)}` : '-' },
    {
      title: '特殊参数', dataIndex: 'default_params', width: 100,
      render: (v) => v ? <Tag color="cyan">已配置</Tag> : <Text type="secondary">无</Text>
    },
    {
      title: '备用模型', width: 100,
      render: (_, record) => {
        try {
          const fb = record.fallback_models ? JSON.parse(record.fallback_models) : {};
          const hasFallback = Object.values(fb).some(v => v && v.length > 0);
          return hasFallback ? (
            <Tag color="orange">已配置</Tag>
          ) : (
            <Text type="secondary">无</Text>
          );
        } catch (e) {
          return <Text type="secondary">无</Text>;
        }
      }
    },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (status, record) => (
        <Switch
          size="small"
          checked={status === 'active'}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={(checked) => handleToggleStatus(record.id, checked)}
        />
      ),
    },
    {
      title: '操作', width: 260, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => {
            setWizardEditingModel(record);
            setModelWizardVisible(true);
          }}>编辑</Button>
          <Button type="link" size="small" onClick={() => {
            setPricingRulesModel(record);
            setPricingRulesVisible(true);
          }}>定价规则</Button>
          <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => handleOpenFallbackModal(record)}>
            备用模型
          </Button>
          <Popconfirm title="确定要删除此模型配置吗？" onConfirm={() => handleDeleteModel(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Select options
  const providerOptions = providers.filter(p => p.status === 'active').map(p => ({ value: p.id, label: p.display_name }));

  // 过滤后的模型数据
  const filteredModels = models.filter(model => {
    const searchLower = modelSearchText.toLowerCase();
    const matchText = !modelSearchText ||
      model.model_name?.toLowerCase().includes(searchLower) ||
      model.display_name?.toLowerCase().includes(searchLower) ||
      model.provider?.display_name?.toLowerCase().includes(searchLower) ||
      model.description?.toLowerCase().includes(searchLower);

    const matchType = !modelTypeFilter || model.type === modelTypeFilter;
    const matchStatus = !modelStatusFilter || model.status === modelStatusFilter;
    const matchProvider = !modelProviderFilter || model.provider_id === modelProviderFilter;
    const matchHot = modelHotFilter === null || model.is_hot === modelHotFilter;
    const matchBilling = modelBillingFilter === null || model.billing_type === modelBillingFilter;
    
    // 备用模型筛选
    let hasFallback = false;
    try {
      const fb = model.fallback_models ? JSON.parse(model.fallback_models) : {};
      hasFallback = Object.values(fb).some(v => v && v.length > 0);
    } catch (e) {}
    const matchFallback = modelFallbackFilter === null || 
      (modelFallbackFilter === true && hasFallback) || 
      (modelFallbackFilter === false && !hasFallback);
    
    // 特殊参数筛选
    const hasParams = !!model.default_params;
    const matchParams = modelParamsFilter === null || 
      (modelParamsFilter === true && hasParams) || 
      (modelParamsFilter === false && !hasParams);

    return matchText && matchType && matchStatus && matchProvider && matchHot && matchBilling && matchFallback && matchParams;
  });

  // 清除筛选
  const clearModelFilters = () => {
    setModelSearchText('');
    setModelTypeFilter(null);
    setModelStatusFilter(null);
    setModelProviderFilter(null);
    setModelHotFilter(null);
    setModelBillingFilter(null);
    setModelFallbackFilter(null);
    setModelParamsFilter(null);
    setModelCurrentPage(1);
  };

  const hasModelFilters = modelSearchText || modelTypeFilter || modelStatusFilter || modelProviderFilter !== null || modelHotFilter !== null || modelBillingFilter !== null || modelFallbackFilter !== null || modelParamsFilter !== null;

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <ApiOutlined style={{ marginRight: 8 }} />
        供应商与模型管理
      </Title>

      {/* 供应商列表 */}
      <Card
        title="供应商列表"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setWizardEditingProvider(null);
            setProviderWizardVisible(true);
          }}>
            添加供应商
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={providerColumns}
          dataSource={providers}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
          size="small"
        />
      </Card>

      {/* 模型配置列表 */}
      <Card
        title="模型配置"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setWizardEditingModel(null);
            setModelWizardVisible(true);
          }}>
            添加模型
          </Button>
        }
      >
        {/* 搜索筛选栏 */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            placeholder="按供应商筛选"
            value={modelProviderFilter}
            onChange={(val) => { setModelProviderFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 200 }}
            options={providers.map(p => ({ value: p.id, label: p.display_name }))}
          />
          <Input
            placeholder="搜索模型..."
            value={modelSearchText}
            onChange={(e) => { setModelSearchText(e.target.value); setModelCurrentPage(1); }}
            style={{ width: 200 }}
            allowClear
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          />
          <Select
            placeholder="类型"
            value={modelTypeFilter}
            onChange={(val) => { setModelTypeFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 220 }}
            options={FIXED_TYPES.map(type => {
              const count = models.filter(m => m.type === type).length;
              const displayName = TYPE_MAPPING[type] || type;
              return { value: type, label: `${displayName} (${count})` };
            })}
          />
          <Select
            placeholder="热门"
            value={modelHotFilter}
            onChange={(val) => { setModelHotFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 90 }}
            options={[
              { value: true, label: '是' },
              { value: false, label: '否' },
            ]}
          />
          <Select
            placeholder="状态"
            value={modelStatusFilter}
            onChange={(val) => { setModelStatusFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 90 }}
            options={[
              { value: 'active', label: '启用' },
              { value: 'inactive', label: '禁用' },
            ]}
          />
          <Select
            placeholder="计费方式"
            value={modelBillingFilter}
            onChange={(val) => { setModelBillingFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 100 }}
            options={[
              { value: 'per_token', label: 'Token' },
              { value: 'per_call', label: '按次' },
              { value: 'per_char', label: '字符' },
              { value: 'per_second', label: '按秒' },
              { value: 'free', label: '免费' },
            ]}
          />
          <Select
            placeholder="备用模型"
            value={modelFallbackFilter}
            onChange={(val) => { setModelFallbackFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 110 }}
            options={[
              { value: true, label: '已配置' },
              { value: false, label: '未配置' },
            ]}
          />
          <Select
            placeholder="特殊参数"
            value={modelParamsFilter}
            onChange={(val) => { setModelParamsFilter(val); setModelCurrentPage(1); }}
            allowClear
            style={{ width: 110 }}
            options={[
              { value: true, label: '已配置' },
              { value: false, label: '未配置' },
            ]}
          />
          {hasModelFilters && (
            <Button type="link" onClick={clearModelFilters}>
              清除筛选
            </Button>
          )}
          <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: 13 }}>
            共 {filteredModels.length} 个模型
          </span>
        </div>
        <Table
          columns={modelColumns}
          dataSource={filteredModels}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600, y: 500 }}
          pagination={{
            pageSize: modelPageSize,
            current: modelCurrentPage,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setModelCurrentPage(page);
              setModelPageSize(pageSize);
            },
          }}
          size="small"
        />
      </Card>

      {/* 供应商向导弹窗 */}
      <ProviderWizard
        visible={providerWizardVisible}
        onCancel={() => {
          setProviderWizardVisible(false);
          setWizardEditingProvider(null);
        }}
        onSuccess={() => {
          setProviderWizardVisible(false);
          setWizardEditingProvider(null);
          fetchProviders();
          adminAPI.refreshGatewayModels().catch(() => {});
        }}
        editingProvider={wizardEditingProvider}
      />

      {/* 模型向导弹窗 */}
      <ModelWizard
        visible={modelWizardVisible}
        onCancel={() => {
          setModelWizardVisible(false);
          setWizardEditingModel(null);
        }}
        onSuccess={() => {
          setModelWizardVisible(false);
          setWizardEditingModel(null);
          fetchModels();
          adminAPI.refreshGatewayModels().catch(() => {});
        }}
        editingModel={wizardEditingModel}
        providers={providers}
      />

      {/* 模型级备用模型配置弹窗 */}
      <Modal
        title="模型级备用模型配置"
        open={fallbackModalVisible}
        onCancel={() => setFallbackModalVisible(false)}
        width={600}
        footer={null}
      >
        <Alert
          message="优先级说明"
          description={<span>用户级 &gt; 模型级 &gt; 平台默认</span>}
          type="info"
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <p><strong>当前模型：</strong>{fallbackEditingModel?.display_name || fallbackEditingModel?.model_name}</p>
        </div>

        {fallbackLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 8, fontWeight: 500 }}>选择模型类型：</span>
              <Select
                style={{ width: 200 }}
                value={selectedFallbackType}
                onChange={(value) => setSelectedFallbackType(value)}
              >
                {Object.entries(TYPE_MAPPING).map(([key, label]) => (
                  <Select.Option key={key} value={key}>{label}</Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>{TYPE_MAPPING[selectedFallbackType]} 备用模型：</Text>
              <Select
                mode="tags"
                style={{ width: '100%', marginTop: 8 }}
                placeholder="选择或输入备用模型名称"
                value={fallbackModels[selectedFallbackType] || []}
                onChange={(value) => setFallbackModels({ ...fallbackModels, [selectedFallbackType]: value })}
              >
                {models.filter(m => m.type === selectedFallbackType && m.status === 'active' && m.id !== fallbackEditingModel?.id).map(m => (
                  <Select.Option key={m.model_name} value={m.model_name}>
                    {m.display_name || m.model_name} ({m.provider?.display_name || ''})
                  </Select.Option>
                ))}
              </Select>
            </div>

            {Object.values(fallbackModels).some(v => v && v.length > 0) && (
              <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <Text strong>当前配置预览：</Text>
                {Object.entries(fallbackModels).filter(([_, v]) => v && v.length > 0).map(([type, modelList]) => (
                  <div key={type} style={{ marginTop: 4 }}>
                    <Text type="secondary">{TYPE_MAPPING[type] || type}: </Text>
                    <Text>{modelList.join(', ')}</Text>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleClearFallback}>清除配置</Button>
                <Button type="primary" onClick={handleSaveFallback}>保存配置</Button>
              </Space>
            </div>
          </>
        )}
      </Modal>

      {/* 定价规则配置弹窗（分时价/缓存价/阶梯价，保存进 metadata.pricing_rules） */}
      <PricingRulesModal
        visible={pricingRulesVisible}
        model={pricingRulesModel}
        onClose={() => setPricingRulesVisible(false)}
        onSaved={() => fetchModels()}
      />
    </div>
  );
};

export default Providers;
